import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../css/booking.css";

const Booking = () => {
  const [dates, setDates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const getMonthYear = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
  };

  useEffect(() => {
    const fetchDates = async () => {
      try {
        const response = await fetch(
          "https://getyourcscs.co.uk/api/available-dates",
        );
        if (!response.ok) throw new Error("Backend not responding");

        const data = await response.json();
        setDates(data);
      } catch (error) {
        console.error(
          "Failed to fetch dates. Ensure backend is running:",
          error,
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchDates();
  }, []);

  const handleBooking = (date) => {
    // Calculate remaining slots to pass to the checkout page
    const remainingSlots = Math.max(10 - date.bookedCount, 0);

    navigate("/checkout", {
      state: {
        dateString: date.dateString,
        dateId: date.id,
        remainingSlots: remainingSlots, // Passed to checkout
      },
    });
  };

  // Group the fetched dates by month for cleaner layout
  const groupedDates = dates.reduce((groups, date) => {
    const month = getMonthYear(date.dateString);
    if (!groups[month]) {
      groups[month] = [];
    }
    groups[month].push(date);
    return groups;
  }, {});

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Course",
    name: "1-Day CSCS Course & Test",
    description:
      "Full day classroom training and official test for your CSCS card.",
    provider: {
      "@type": "Organization",
      name: "Alpha Construction Training",
      sameAs: "https://www.getyourcscs.co.uk",
    },
    hasCourseInstance: {
      "@type": "CourseInstance",
      courseMode: "Onsite",
      location: {
        "@type": "Place",
        name: "Walsall & West Bromwich Training Centres",
      },
    },
  };

  return (
    <div className="page-wrapper flat-dark-theme">
      <Helmet>
        <title>Get Your CSCS | Booking</title>
        <meta
          name="description"
          content="Book your 1-day CSCS classroom training and test in Walsall or West Bromwich. All-inclusive £400 package with weekend availability. Secure your slot today."
        />
        <meta
          name="keywords"
          content="CSCS course, CSCS test, book CSCS card, Walsall CSCS, West Bromwich construction training, Green CSCS card test"
        />

        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://www.getyourcscs.co.uk/booking"
        />
        <meta
          property="og:title"
          content="Book CSCS Course & Test | Walsall & West Bromwich"
        />
        <meta
          property="og:description"
          content="Secure your 1-day CSCS training and test slot. Weekend courses available now."
        />

        <link rel="canonical" href="https://www.getyourcscs.co.uk/booking" />

        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
      </Helmet>

      <div className="booking-hero">
        <h2 className="section-title">Select Your Training Date</h2>
        <p className="intro-text">
          1-day classroom training and test. Slots fill up quickly, book in
          advance.
        </p>
        <div className="quick-badges">
          <span className="badge">⏰ 09:00 - 17:00</span>
          <span className="badge">📍 West Brom</span>
        </div>
      </div>

      <div className="section-container booking-main">
        {isLoading ? (
          <div className="empty-state">Loading available dates...</div>
        ) : dates.length === 0 ? (
          <div className="empty-state">
            No training dates are currently available. Please check back later.
          </div>
        ) : (
          <div className="grouped-dates-container">
            {Object.entries(groupedDates).map(([monthLabel, monthDates]) => (
              <div key={monthLabel} className="month-section">
                <h3 className="month-section-title">{monthLabel}</h3>

                <div className="dates-grid">
                  {monthDates.map((date) => {
                    const isFull = date.bookedCount >= 10;
                    const progressPercentage = (date.bookedCount / 10) * 100;

                    return (
                      <div
                        key={date.id}
                        className={`flat-booking-card ${isFull ? "card-disabled" : ""}`}
                      >
                        <div className="card-top">
                          <h3>{date.dateString}</h3>
                        </div>

                        <div className="slot-indicator">
                          <div className="slot-header">
                            <span
                              className={`status-text ${isFull ? "text-red" : "text-green"}`}
                            >
                              {isFull ? "Fully Booked" : "Available"}
                            </span>
                            <span className="slot-count">
                              {isFull ? "Sold Out" : "Limited Slots!"}
                            </span>
                          </div>
                        </div>

                        <button
                          className={`btn-secure ${isFull ? "btn-disabled" : ""}`}
                          onClick={() => handleBooking(date)}
                          disabled={isFull}
                        >
                          {isFull
                            ? "Unavailable"
                            : "Secure Slot (£100 Deposit)"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Booking;
